/**
 * Provides methods for issuing API requests.
 * @module api/request
 * @private
 */
const axios = require('axios');
const axiosRetry = require('axios-retry');

const errors = require('./errors');
const {base} = require('./urls');
// const authStore = require('./auth-store');

axios.defaults.baseURL = base();
axios.defaults.headers.Accept = 'application/json';
axios.defaults.headers['Content-Type'] = 'application/json';
/**
 * Set the default Auth Token to be used with Auth Header
 * @param {string} token A JWT token.
 */
const setAuthToken = token => {
  axios.defaults.authToken = token;
};

/**
 * Check if the response represents an error.
 * @param {IncomingMessage} response The response.
 * @param {Object} body Any parsed body (as JSON).
 * @return {errors.ResponseError} A response error (or null if none).
 */
const errorCheck = (response, body) => {
  let err = null;
  const status = response.status;
  if (status === 400) {
    err = new errors.BadRequest('Bad request', response, body);
  } else if (status === 401) {
    err = new errors.Unauthorized('Unauthorized', response, body);
  } else if (status === 403) {
    err = new errors.Forbidden('Forbidden', response, body);
  } else if (!(status >= 200 && status < 300)) {
    err = new errors.UnexpectedResponse(
      'Unexpected response status: ' + status,
      response,
      body
    );
  }
  return err;
};

const handleError = error => {
  let err = null;
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error
  if (error.response) {
    err = errorCheck(error.response, error.response.data);
  } else if (error.request) {
    err = new errors.ClientError('Request failed');
  } else {
    err = error;
  }

  return Promise.reject(err);
};

const handleRequest = config => {
  if (config.authToken) {
    config.headers.Authorization = `Bearer ${config.authToken}`;
  }
  // override default retries
  if ('retries' in config) {
    config['axios-retry'] = {
      retries: config.retries,
    };
  }

  return config;
};

// Add a request interceptor
axios.interceptors.request.use(handleRequest, handleError);

// Add a response interceptor
axios.interceptors.response.use(function (response) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response;
}, handleError);

const SAFE_HTTP_METHODS = ['get', 'head', 'options'];
const retryCodes = [429];

const isRetryableError = error =>
  !!error.response && retryCodes.indexOf(error.response.status) !== -1;

const retryCondition = error =>
  axiosRetry.isNetworkOrIdempotentRequestError(error) ||
  (isRetryableError(error) &&
    SAFE_HTTP_METHODS.indexOf(error.config.method) !== -1);

axiosRetry(axios, {
  retryCondition,
  retryDelay: axiosRetry.exponentialDelay,
});

/**
 * Set X-Planet-App header
 * @param {*} appName - Application name making the requests
 * @param {*} matchUrls - base urls to tack the header to
 */
const setAppHeader = (appName, matchUrls = []) => {
  axios.interceptors.request.use(config => {
    if (
      matchUrls.length === 0 ||
      matchUrls.some(testUrl => config.url.includes(testUrl))
    ) {
      config.headers = {
        ...config.headers,
        'X-Planet-App': appName,
      };
    }
    return config;
  });
};

const request = (module.exports = axios);
request.setAuthToken = setAuthToken;
request.setAppHeader = setAppHeader;
